import { styled } from "goober";
import { useCallback } from "react";
import { formatBytes } from "../../lib/formatBytes";
import { Icon } from "../Icon";
import { metaJoin } from "../MetaSeparator";
import { Text } from "../Text";
import { triggerDownload } from "./download";
import { DownloadButton } from "./DownloadButton";
import { useObjectUrl } from "./useObjectUrl";
import { Tooltip } from "../Tooltip";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;
  height: 100%;
`;

const Frame = styled("iframe")`
  flex: 1;
  min-height: 480px;
  width: 100%;
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
`;

const MetaRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

type Props = {
  url?: string;
  fileName?: string;
};

export function Pdf({ url, fileName }: Props) {
  const { blobUrl, size: fileSize } = useObjectUrl(url);

  const downloadName = fileName ?? "output.pdf";
  const download = useCallback(() => {
    if (blobUrl) triggerDownload(blobUrl, downloadName);
  }, [blobUrl, downloadName]);

  if (!blobUrl) return null;

  const metaParts = ["PDF"];
  if (fileSize != null) metaParts.push(formatBytes(fileSize));

  return (
    <Container>
      <Frame src={`${blobUrl}#toolbar=0&navpanes=0`} title={downloadName} />
      <MetaRow>
        <Text variant="text-small" color="var(--text-secondary)">
          {metaJoin(metaParts)}
        </Text>
        <div style={{ marginLeft: "auto" }}>
          <Tooltip content="Download" align="center">
            <DownloadButton onPress={download} disabled={!blobUrl}>
              <Icon name="arrow-down" size={12} />
              <Text variant="text-small-md">Download</Text>
            </DownloadButton>
          </Tooltip>
        </div>
      </MetaRow>
    </Container>
  );
}
